import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Loader, Message } from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import Toolbar from 'components/common/Toolbar';
import ToolbarFooter from 'components/common/Toolbar/ToolbarFooter';
import Transactions from 'components/Wallet/Transactions';
import { GROUP_WALLET } from 'constants/walletTransactionsContext';
import SingleGroupWalletChart from 'components/Wallet/SingleGroupWalletChart';
import Empty from 'components/common/Empty';
import CreditSidebar from 'components/common/SidebarFilter/CreditSidebar';
import getGroupsTransaction from 'redux/actions/groups/getGroupsTransaction';
import capitalize from 'utils/capitalize';
import getUserRoles from 'helpers/getUserRoles';
import { canPerformAction } from 'helpers/permissions';
import DropdownFilterYear from 'components/common/DropdownFilterYear';

const SingleGroupWallet = ({ match }) => {
  const dispatch = useDispatch();
  const [showCreditSidebar, setShowCreditSidebar] = useState(false);
  const [filterYear, setFilterYear] = useState(null);
  const { roles, currentOrgId } = getUserRoles();
  const { search: searchQueries } = useLocation();
  const queryData = queryString.parse(searchQueries) || {};

  const groupId = Number(match?.params?.groupId);

  const {
    loading,
    data: transactions,
    monthlyBalances,
    group: groupData,
    error,
    meta,
  } = useSelector(
    ({ groups: { groupTransactions } }) => groupTransactions,
  );

  const group = groupData || { id: groupId, name: '' };

  const fetchTransactions = (queries = {}) => {
    const base = { group_id: groupId, ...queryData, ...queries };
    if (filterYear) base.year = filterYear;
    if (base.year === 'all') delete base.year;
    getGroupsTransaction(base)(dispatch);
  };

  useEffect(() => {
    fetchTransactions();
  }, [groupId, filterYear]);

  const onQuery = queries => {
    fetchTransactions(queries);
  };

  return (
    <div className="wallet">
      <Toolbar
        header={{
          navigation: true,
          title: capitalize(group.name),
          titleComponent: (
            <h2>
              {capitalize(group.name)}
              <span className="group-wallet-id-badge">
                ID {group.id}
              </span>
            </h2>
          ),
          rightComponent: (
            <>
              <DropdownFilterYear
                handleChange={() => {}}
                requestHandledBySelf
                setFilterYear={val =>
                  setFilterYear(val === 'all' ? null : val)
                }
              />
              {canPerformAction('walletCredit', roles) && (
                <Button
                  primary
                  onClick={() => setShowCreditSidebar(true)}
                >
                  Credit
                </Button>
              )}
            </>
          ),
        }}
        body={
          <SingleGroupWalletChart
            monthlyBalances={monthlyBalances}
            group={group}
            filterYear={filterYear}
            loading={loading}
          />
        }
        footer={
          <Toolbar
            ignoreHelmet
            header={{
              title: 'All Transactions',
              rightComponent: (
                <ToolbarFooter
                  onSearch={onQuery}
                  onPaginate={onQuery}
                  paginationMeta={meta}
                  exportCSVData={transactions}
                  exportCSVFileName={`${group.name}-transactions`}
                  filterTransactions
                  onFilterTransactions={onQuery}
                  filterTransactionsOptions={[
                    'pending',
                    'success',
                    'failed',
                  ]}
                />
              ),
            }}
          />
        }
        backUrl="/wallets/group"
      />

      <>
        {error && <Message negative>{error.message}</Message>}
        {loading && <Loader active inline="centered" />}

        {!loading && !error && transactions?.length === 0 && (
          <Empty message="No transactions found" />
        )}

        {!loading && !error && transactions?.length > 0 && (
          <Transactions
            currentOrgName={group.name}
            items={transactions}
            roles={roles}
            context={GROUP_WALLET}
            viewAll
            showGroupDetails
            onTransactionRefresh={() => fetchTransactions()}
          />
        )}
      </>

      <CreditSidebar
        filterOn={showCreditSidebar}
        setActionType={val => setShowCreditSidebar(!!val)}
        currentOrgId={currentOrgId}
        groupId={groupId}
      />
    </div>
  );
};

SingleGroupWallet.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      groupId: PropTypes.string,
    }),
  }).isRequired,
};

export default SingleGroupWallet;
